'use client';

import dynamic from 'next/dynamic';
import { MapPin } from 'lucide-react';
import Image from 'next/image';
import CardExpandButton from '@/components/shared/card-expand-button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import {
  formatProjectType,
  getProjectDisplayImage,
  getProjectPhotoImages,
  type SanityProject,
} from '@/sanity/project-data';

const ProjectDetails = dynamic(
  () => import('@/components/shared/project-details').then((mod) => mod.ProjectDetails),
  { ssr: false },
);

export default function ProjectCard({ project }: { project: SanityProject }) {
  const isCompactTouch = useIsCompactTouch();
  const image = getProjectDisplayImage(project);
  if (!image) return null;

  const projectImages = getProjectPhotoImages(project);
  const details = {
    src: image.src,
    title: project.title,
    location: project.location ?? undefined,
    type: project.type,
    description: project.description ?? undefined,
    delivered: project.delivered ?? undefined,
    duration: project.duration ?? undefined,
    status: project.status ?? undefined,
    mediaSections: [
      {
        id: 'photos',
        label: 'Images',
        images: projectImages,
      },
      {
        id: 'renderings',
        label: 'Renderings',
        images: project.renders,
      },
    ],
    beforeAfter: project.beforeAfter.map((comparison) => ({
      title: comparison.title,
      before: {
        src: comparison.beforeSrc,
        alt: comparison.beforeAlt,
        title: comparison.title,
      },
      after: {
        src: comparison.afterSrc,
        alt: comparison.afterAlt,
        title: comparison.title,
      },
    })),
  };

  const trigger = (
    <button
      type="button"
      aria-label={`View ${project.title}`}
      className="group relative aspect-video h-auto w-full cursor-pointer overflow-hidden rounded-xl border border-white/20 dark:border-white/10 bg-black text-left transition-all duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-white/40"
    >
      <Image
        src={image.src}
        alt={image.alt ?? ''}
        fill
        sizes="(max-width: 767px) calc(100vw - 2rem), (max-width: 1279px) calc((100vw - 3rem) / 2), 411px"
        className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
      />
      <div className="pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(180deg,rgba(0,0,0,0.24)_0%,rgba(0,0,0,0)_26%,rgba(0,0,0,0)_38%,rgba(0,0,0,0.82)_100%)]" />
      <Badge className="absolute top-4 left-4 rounded-full z-40 bg-white/10 border-[0.5px] border-white/20 backdrop-blur-sm text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest w-fit">
        {project.category?.title ?? formatProjectType(project.type)}
      </Badge>
      <CardExpandButton />
      <div className="absolute inset-x-4 bottom-4 text-white z-40">
        <div className="text-base sm:text-lg font-semibold leading-tight">{project.title}</div>
        {project.location ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-white/75">
            <MapPin className="size-3 shrink-0" />
            {project.location}
          </div>
        ) : null}
      </div>
    </button>
  );

  if (isCompactTouch) {
    return (
      <Drawer>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          showHandle={false}
          className="h-fit max-h-[88vh] overflow-hidden border-0 p-0 shadow-none ring-0 dark:bg-neutral-950 dark:border-t dark:border-neutral-700"
        >
          <DrawerTitle className="sr-only">{project.title}</DrawerTitle>
          <ProjectDetails item={details} className="max-h-[calc(88vh-2.25rem)] overflow-y-auto" />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-hidden rounded-xl border-none bg-transparent p-0 shadow-2xl sm:max-w-[min(1120px,calc(100vw-2rem))]">
        <DialogHeader className="sr-only">
          <DialogTitle>{project.title}</DialogTitle>
          {project.location ? <DialogDescription>{project.location}</DialogDescription> : null}
        </DialogHeader>
        <ProjectDetails item={details} />
      </DialogContent>
    </Dialog>
  );
}
